import React, { useState, useEffect } from "react";
import { Activity, LayoutList, Grid3x3, Microscope, GitCompare } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import MobileMarkets from "@/components/dashboard/MobileMarkets";
import MobileScreener from "@/components/dashboard/MobileScreener";
import SectorHeatmap from "@/components/dashboard/SectorHeatmap";
import DeepDive from "@/components/dashboard/DeepDive";
import CompareTray from "@/components/dashboard/CompareTray";
import PullToRefresh from "@/components/dashboard/PullToRefresh";

const TABS = [
  { key: "markets", label: "Markets", icon: Activity },
  { key: "screener", label: "Screener", icon: LayoutList },
  { key: "heatmaps", label: "Heatmaps", icon: Grid3x3 },
  { key: "deep", label: "Deep Dive", icon: Microscope },
];

const US_SECTORS = [
  { t: "XLK", name: "Technology" }, { t: "XLF", name: "Financials" }, { t: "XLE", name: "Energy" },
  { t: "XLV", name: "Health" }, { t: "XLY", name: "Cons Disc" }, { t: "XLP", name: "Staples" },
  { t: "XLI", name: "Industrial" }, { t: "XLB", name: "Materials" }, { t: "XLU", name: "Utilities" },
  { t: "XLRE", name: "Real Est" }, { t: "XLC", name: "Comm Svc" },
];
const IN_SECTORS = [
  { t: "^NSEBANK", name: "Bank" }, { t: "^CNXIT", name: "IT" }, { t: "^CNXAUTO", name: "Auto" },
  { t: "^CNXPHARMA", name: "Pharma" }, { t: "^CNXFMCG", name: "FMCG" }, { t: "^CNXMETAL", name: "Metal" },
  { t: "^CNXREALTY", name: "Realty" }, { t: "^CNXENERGY", name: "Energy" },
];

export default function MobileWorkspace({
  status, indices, items, universe, setUniverse, signalFilter, setSignalFilter,
  q, setQ, selected, setSelected, compare, addCompare, removeCompare, loading, running, scan, onRefresh,
}) {
  const [tab, setTab] = useState("markets");
  const [deepOpen, setDeepOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  const openDeep = (t) => { setSelected(t); setDeepOpen(true); };

  // Clearing selection closes the sheet
  useEffect(() => { if (!selected) setDeepOpen(false); }, [selected]);

  const onTab = (key) => {
    if (key === "deep") { setDeepOpen(true); return; }
    setDeepOpen(false);
    setTab(key);
  };

  const activeNav = deepOpen ? "deep" : tab;

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="mobile-workspace">
      <div className="flex-1 flex flex-col min-h-0">
        {tab === "markets" && (
          <MobileMarkets status={status} indices={indices} running={running} scan={scan} onRefresh={onRefresh} />
        )}
        {tab === "screener" && (
          <MobileScreener
            items={items} universe={universe} setUniverse={setUniverse}
            signalFilter={signalFilter} setSignalFilter={setSignalFilter}
            q={q} setQ={setQ} onSelect={openDeep} loading={loading} onRefresh={onRefresh}
          />
        )}
        {tab === "heatmaps" && (
          <PullToRefresh onRefresh={onRefresh} className="flex-1 min-h-0 bg-[#F1F5F9] p-3 space-y-3" testid="mobile-heatmaps">
            <SectorHeatmap
              title="US Sector Heatmap" testid="sector-heatmap" universe="etf"
              onSelect={openDeep} refreshKey={status?.last_scan?.finished_at} sectors={US_SECTORS}
            />
            <SectorHeatmap
              title="India Sector Heatmap (NSE)" testid="sector-heatmap-in" universe="etf"
              onSelect={openDeep} refreshKey={status?.last_scan?.finished_at} sectors={IN_SECTORS}
            />
          </PullToRefresh>
        )}
      </div>

      {/* Compare floating pill */}
      {compare.length > 0 && !deepOpen && !compareOpen && (
        <button
          onClick={() => setCompareOpen(true)}
          data-testid="mobile-compare-pill"
          className="fixed left-1/2 -translate-x-1/2 bottom-[76px] z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0070BA] text-white shadow-lg text-[13px] font-semibold active:bg-[#005B98]"
        >
          <GitCompare size={15} /> Compare {compare.length}
        </button>
      )}

      {/* Bottom tab bar */}
      <nav className="shrink-0 bg-[#121D28] border-t border-[#1E2D3E] grid grid-cols-4 z-30" data-testid="mobile-bottom-nav" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeNav === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onTab(t.key)}
              data-testid={`mobile-nav-${t.key}`}
              className="flex flex-col items-center justify-center gap-1 py-2.5 transition-colors"
              style={{ color: active ? "#38BDF8" : "#94A3B8" }}
            >
              <Icon size={20} strokeWidth={active ? 2.4 : 2} />
              <span className="text-[10px] font-semibold tracking-wide">{t.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Deep Dive slide-up sheet */}
      <Sheet open={deepOpen} onOpenChange={(o) => { setDeepOpen(o); if (!o) setSelected(null); }}>
        <SheetContent side="bottom" className="h-[92vh] p-0 bg-[#F1F5F9] border-t border-[#E2E8F0] flex flex-col rounded-t-[14px]" data-testid="mobile-deepdive-sheet">
          <div className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-slate-300 shrink-0" />
          <div className="flex-1 min-h-0 p-2 pt-1">
            <DeepDive ticker={selected} onCompare={(t) => addCompare(t)} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Compare slide-up sheet */}
      <Sheet open={compareOpen} onOpenChange={setCompareOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] p-0 bg-[#F1F5F9] border-t border-[#E2E8F0] flex flex-col rounded-t-[14px]" data-testid="mobile-compare-sheet">
          <div className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-slate-300 shrink-0" />
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <CompareTray
              tickers={compare}
              onRemove={removeCompare}
              onSelect={(t) => { setCompareOpen(false); openDeep(t); }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
